// Wolf runner.
//
// 4-ball game. The Wolf rotates by hole — P1, P2, P3, P4, then repeats — and
// tees off first. After the tee shots the Wolf either takes a partner or plays
// solo. The live per-hole picker (WolfSelectionModal) records the decision:
//   • a partnerId  → 2-v-2: Wolf + partner vs the other two
//   • Lone Wolf    → solo (1 v 3), declared after the shots
//   • Blind Wolf   → solo (1 v 3), declared BEFORE any tee shot — its win/loss
//                    is multiplied by the blind multiplier (Double / Triple)
//
// Settlement (zero-sum), per the agreed model:
//   • Sides are compared by better-ball — the lower (best) score on each side,
//     gross or net per the option. Net uses the same WHS course-handicap +
//     stroke-index allocation the other runners use.
//   • A win collects `betPerPlayer` from each opponent. So in 2-v-2 each winner
//     is +2 units (one from each of the two losers) and each loser −2. Solo the
//     Wolf faces all three: win = +3 (one from each), lose = −3, each opponent
//     the mirror ±1. A Blind Wolf hole multiplies all those transfers.
//   • Ties push — no money moves, nothing carries.
//
// A hole with no recorded decision, or missing any of the four scores, is
// skipped (no settlement) so a partial round renders cleanly. Pure function —
// no React, no state.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";
import { WolfSelections } from "../activeRound";

// WHS-style course handicap — duplicated from the other runners so this file
// stays standalone.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type WolfScoringMode = "net" | "gross";

export interface WolfOptions {
  betPerPlayer?: number;
  // Master toggle for the Blind Wolf option. When off, a "blind" selection
  // settles like a plain Lone Wolf (×1) — defensive, since the picker also
  // hides Blind Wolf when this is off.
  blindEnabled?: boolean;
  // Stored as a string ("2".."5") by the catalog select; parsed here.
  blindMultiplier?: string | number;
  scoringMode?: WolfScoringMode;
  // Handicap policy ("full" | "playOffLow") — only meaningful in net.
  handicapMode?: HandicapMode;
}

export interface WolfPayout {
  // Net cash for the round. Positive = up.
  total: number;
  // Per-hole detail rows for the leaderboard's hole-expansion view.
  details: Array<{ hole: number; delta: number; note: string }>;
}

export interface WolfResult {
  payouts: Record<string, WolfPayout>;
  // [holeNumber][playerId] → that player's net delta on that hole. Decided
  // holes only; undecided/partial holes emit no entry.
  perHole: Record<number, Record<string, number>>;
  // 1-based hole number → the Wolf's playerId that hole (rotation), so the
  // leaderboard/scorecard can label whose call it was.
  wolfByHole: Record<number, string>;
  // True when the bet is > 0 AND the round has exactly four players — the only
  // shape that produces useful output.
  hasAnyBet: boolean;
}

export interface WolfInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  selections: WolfSelections;
  options: WolfOptions;
}

function emptyResult(players: Player[], hasAnyBet: boolean): WolfResult {
  const payouts: Record<string, WolfPayout> = {};
  for (const p of players) payouts[p.id] = { total: 0, details: [] };
  return { payouts, perHole: {}, wolfByHole: {}, hasAnyBet };
}

export function runWolf(input: WolfInput): WolfResult {
  const { course, players, scores, selections, options } = input;
  const bet = Number(options.betPerPlayer) || 0;
  const blindEnabled = options.blindEnabled !== false;
  const blindMult = Number(options.blindMultiplier) || 2;
  const scoringMode: WolfScoringMode =
    options.scoringMode === "net" ? "net" : "gross";

  // Wolf needs exactly four players; the catalog gate enforces it but a stored
  // round could reach here mis-shaped.
  if (players.length !== 4 || bet <= 0) {
    return emptyResult(players, false);
  }

  const result = emptyResult(players, true);
  const { payouts, perHole, wolfByHole } = result;

  // Course-handicap cache for net scoring. Apply the handicap policy first
  // (identity in "full" / gross; lowest-anchored in net "playOffLow").
  const adjusted =
    scoringMode === "net"
      ? adjustHandicaps(players, options.handicapMode)
      : players;
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of adjusted) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const scoreFor = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    if (scoringMode === "gross") return sc.gross;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  const award = (
    hole: Hole,
    winners: Player[],
    losers: Player[],
    perWinner: number,
    note: string,
  ) => {
    perHole[hole.number] = {};
    for (const w of winners) {
      payouts[w.id].total += perWinner;
      payouts[w.id].details.push({ hole: hole.number, delta: perWinner, note });
      perHole[hole.number][w.id] = perWinner;
    }
    for (const l of losers) {
      payouts[l.id].total -= perWinner;
      payouts[l.id].details.push({
        hole: hole.number,
        delta: -perWinner,
        note,
      });
      perHole[hole.number][l.id] = -perWinner;
    }
  };

  for (const hole of course.holes) {
    const wolf = players[(hole.number - 1) % players.length];
    wolfByHole[hole.number] = wolf.id;

    const sel = selections[hole.number];
    if (!sel) continue; // no decision recorded yet

    // Need every player's score on this hole to settle either shape.
    const scoreById: Record<string, number> = {};
    let complete = true;
    for (const p of players) {
      const s = scoreFor(p.id, hole);
      if (s == null) {
        complete = false;
        break;
      }
      scoreById[p.id] = s;
    }
    if (!complete) continue;

    const others = players.filter((p) => p.id !== wolf.id);

    if (sel.partnerId) {
      // 2-v-2: Wolf + partner vs the other two.
      const partner = players.find((p) => p.id === sel.partnerId);
      if (!partner) continue; // stale partner id — skip
      const wolfSide = [wolf, partner];
      const oppSide = others.filter((p) => p.id !== partner.id);
      const wolfBest = Math.min(...wolfSide.map((p) => scoreById[p.id]));
      const oppBest = Math.min(...oppSide.map((p) => scoreById[p.id]));
      if (wolfBest === oppBest) continue; // tie → push
      // Each winner collects `bet` from each of the two opponents → ±2·bet.
      const perWinner = bet * oppSide.length;
      if (wolfBest < oppBest) {
        award(hole, wolfSide, oppSide, perWinner, `Wolf + partner win`);
      } else {
        award(hole, oppSide, wolfSide, perWinner, `Wolf + partner lose`);
      }
    } else {
      // Solo (Lone or Blind): Wolf vs all three. Blind multiplies the hole,
      // but only while the Blind Wolf option is enabled.
      const mult = sel.blind && blindEnabled ? blindMult : 1;
      const wolfScore = scoreById[wolf.id];
      const oppBest = Math.min(...others.map((p) => scoreById[p.id]));
      if (wolfScore === oppBest) continue; // tie → push
      const label = sel.blind ? "Blind Wolf" : "Lone Wolf";
      // Wolf wins/loses `bet` against each of the three opponents, ×mult.
      const perOpponent = bet * mult;
      if (wolfScore < oppBest) {
        // Wolf wins: +bet from each opponent.
        payouts[wolf.id].total += perOpponent * others.length;
        payouts[wolf.id].details.push({
          hole: hole.number,
          delta: perOpponent * others.length,
          note: `${label} wins`,
        });
        perHole[hole.number] = { [wolf.id]: perOpponent * others.length };
        for (const op of others) {
          payouts[op.id].total -= perOpponent;
          payouts[op.id].details.push({
            hole: hole.number,
            delta: -perOpponent,
            note: `${label} wins`,
          });
          perHole[hole.number][op.id] = -perOpponent;
        }
      } else {
        // Wolf loses: pays bet to each opponent.
        payouts[wolf.id].total -= perOpponent * others.length;
        payouts[wolf.id].details.push({
          hole: hole.number,
          delta: -perOpponent * others.length,
          note: `${label} loses`,
        });
        perHole[hole.number] = { [wolf.id]: -perOpponent * others.length };
        for (const op of others) {
          payouts[op.id].total += perOpponent;
          payouts[op.id].details.push({
            hole: hole.number,
            delta: perOpponent,
            note: `${label} loses`,
          });
          perHole[hole.number][op.id] = perOpponent;
        }
      }
    }
  }

  return result;
}
